import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import {
  dunsRegex,
  eoriRegex,
  rcsRegex,
  registrationIdSchema,
  vatRegex,
} from "~/utils/schemas/registration-id.schema"

export function useRegistrationIdSchema(): z.ZodType<
  z.infer<typeof registrationIdSchema>
> {
  const { t } = useTranslation("supplier")

  return useMemo(
    () =>
      z.discriminatedUnion("type", [
        z.object({
          id: z.string().optional(),
          type: z.literal("VAT"),
          value: z
            .string()
            .regex(vatRegex, {
              error: t("productionSiteModal.registrationIdErrors.VAT"),
            })
            .describe(t("productionSiteModal.registrationIdDescriptions.VAT")),
        }),
        z.object({
          id: z.string().optional(),
          type: z.literal("EORI"),
          value: z
            .string()
            .regex(eoriRegex, {
              error: t("productionSiteModal.registrationIdErrors.EORI"),
            })
            .describe(t("productionSiteModal.registrationIdDescriptions.EORI")),
        }),
        z.object({
          id: z.string().optional(),
          type: z.literal("GLN"),
          value: z
            .string()
            .min(13, {
              error: t("productionSiteModal.registrationIdErrors.GLN"),
            })
            .describe(t("productionSiteModal.registrationIdDescriptions.GLN")),
        }),
        z.object({
          id: z.string().optional(),
          type: z.literal("DUNS"),
          value: z
            .string()
            .regex(dunsRegex, {
              error: t("productionSiteModal.registrationIdErrors.DUNS"),
            })
            .describe(t("productionSiteModal.registrationIdDescriptions.DUNS")),
        }),
        z.object({
          id: z.string().optional(),
          type: z.literal("RCS"),
          value: z
            .string()
            .regex(rcsRegex, {
              error: t("productionSiteModal.registrationIdErrors.RCS"),
            })
            .describe(t("productionSiteModal.registrationIdDescriptions.RCS")),
        }),
        z.object({
          id: z.string().optional(),
          type: z.literal("OTHER"),
          value: z
            .string()
            .max(32, {
              error: t("productionSiteModal.registrationIdErrors.OTHER"),
            })
            .describe(
              t("productionSiteModal.registrationIdDescriptions.OTHER")
            ),
        }),
      ]),
    [t]
  )
}
